import { jsPDF } from "jspdf";
import "jspdf-autotable";
import {
  dateToDateString,
  convertDecimalAfterComma,
  formatPriceWithoutCurrency,
  ellipsisString,
  formatByCountry
} from "@/app/infrastructures/misc/Utils";
import { StiDestManifestData } from "@/domain/entities/StiDest";
import { AccountController } from "@/app/ui/controllers/AccountController";

let doc: any;

const drawCellDataConditions = (cell: any) => [
  cell.column.dataKey === "totalPieces" &&
    (cell.row.section === "head" || cell.row.section === "body"),
  cell.column.dataKey === "grossWeight" &&
    (cell.row.section === "head" || cell.row.section === "body"),
  cell.column.dataKey === "volumeWeight" &&
    (cell.row.section === "head" || cell.row.section === "body"),
  cell.column.dataKey === "productType" && cell.row.section === "head",
  cell.column.dataKey === "productType" && cell.row.section === "body",
  cell.column.dataKey === "origin" && cell.row.section === "body",
  cell.column.dataKey === "destination" && cell.row.section === "body"
];

const drawCellData = (cell: any) => {
  if (cell.row.index < 0) {
    return;
  }
  if (drawCellDataConditions(cell)[0]) {
    cell.row.cells["totalPieces"].styles = {
      ...cell.row.cells["totalPieces"].styles,
      halign: "center"
    };
  }
  if (drawCellDataConditions(cell)[1]) {
    cell.row.cells["grossWeight"].styles = {
      ...cell.row.cells["grossWeight"].styles,
      halign: "center"
    };
  }
  if (drawCellDataConditions(cell)[2]) {
    cell.row.cells["volumeWeight"].styles = {
      ...cell.row.cells["volumeWeight"].styles,
      halign: "center"
    };
  }
  if (drawCellDataConditions(cell)[3]) {
    cell.row.cells["productType"].styles = {
      ...cell.row.cells["productType"].styles,
      cellPadding: {
        ...cell.row.cells["productType"].styles.cellPadding,
        left: 0.7
      }
    };
  }

  if (drawCellDataConditions(cell)[4]) {
    doc.setFillColor("#ebecf0");
    cell.row.cells["productType"].text
      .filter((item: any) => item !== "")
      .map((_: any, index: number) => {
        doc.roundedRect(
          cell.row.cells["productType"].x + 0.6, //x
          cell.row.cells["productType"].y + //y
            cell.row.cells["productType"].contentHeight /
              (1.6333 +
                1.75 *
                  cell.row.cells["origin"].text.filter(
                    (item: any) => item !== ""
                  ).length) +
            (0.0083 + 5.675 * index),
          cell.row.cells["productType"].contentWidth - 1,
          3,
          1,
          1,
          "F"
        );
      });
    doc.setFillColor("#fff");
  }

  if (drawCellDataConditions(cell)[5]) {
    doc.setFillColor("#ebecf0");
    cell.row.cells["origin"].text
      .filter((item: any) => item !== "")
      .map((_: any, index: number) => {
        doc.roundedRect(
          cell.row.cells["origin"].x + 0.6, //x
          cell.row.cells["origin"].y + //y
            cell.row.cells["origin"].contentHeight /
              (1.6333 +
                1.75 *
                  cell.row.cells["destination"].text.filter(
                    (item: any) => item !== ""
                  ).length) +
            (0.0083 + 5.675 * index),
          cell.row.cells["origin"].contentWidth - 1,
          3,
          1,
          1,
          "F"
        );
      });
    doc.setFillColor("#fff");
  }

  if (drawCellDataConditions(cell)[6]) {
    doc.setFillColor("#ebecf0");
    cell.row.cells["destination"].text
      .filter((item: any) => item !== "")
      .map((_: any, index: number) => {
        doc.roundedRect(
          cell.row.cells["destination"].x + 0.6, //x
          cell.row.cells["destination"].y + //y
            cell.row.cells["destination"].contentHeight /
              (1.6333 +
                1.75 *
                  cell.row.cells["origin"].text.filter(
                    (item: any) => item !== ""
                  ).length) +
            (0.0083 + 5.675 * index),
          cell.row.cells["destination"].contentWidth - 1,
          3,
          1,
          1,
          "F"
        );
      });
    doc.setFillColor("#fff");
  }
};

const generateManifest = (
  head: StiDestManifestData,
  data: any,
  logo: string
) => {
  doc = new jsPDF("p", "mm", "a4", true);
  const pdfWidth = doc.internal.pageSize.width;
  const logoProperties = doc.getImageProperties(logo);
  const logoAspectRatio = logoProperties.height / logoProperties.width;

  const imageWidth = 40;
  const imageHeight = logoAspectRatio * 40;
  doc.addImage(
    logo,
    "PNG",
    pdfWidth - imageWidth - 9,
    11,
    imageWidth,
    imageHeight,
    "logo",
    "FAST",
    0
  );
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor("#4d4d4d");
  doc.text("STI-DEST Manifest", 10, 18);
  doc.setFillColor("#e0e0e0");
  doc.rect(10, 25, 190, 0.2, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor("#4d4d4d");
  // left column
  doc.text("Nama Consolidator", 10, 31);
  doc.text(":", 43, 31);
  doc.text(ellipsisString(head.stiDestCPartnerName, 70), 45, 31);

  doc.setTextColor("#4d4d4d");
  doc.text("Kota Kedatangan", 10, 37);
  doc.text(":", 43, 37);
  doc.text(`${head.stiDestCityCode} - ${head.stiDestCityName}`, 45, 37);

  doc.setTextColor("#4d4d4d");
  doc.text("Tanggal STI-DEST", 10, 43);
  doc.text(":", 43, 43);
  doc.text(dateToDateString(head.stiDestCreatedAt), 45, 43);

  // right column
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor("#4d4d4d");
  doc.text("Total STT", 110, 31);
  doc.text(":", 138, 31);
  doc.text(String(head.stiDestTotalStt), 140, 31);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor("#4d4d4d");
  doc.text("Total Koli", 110, 37);
  doc.text(":", 138, 37);
  doc.text(String(head.stiDestTotalPieces), 140, 37);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor("#4d4d4d");
  doc.text("Total Berat Kotor", 110, 43);
  doc.text(":", 138, 43);
  doc.text(
    `${formatPriceWithoutCurrency(
      Number(convertDecimalAfterComma(head.stiDesttotalGrossWeight, 2))
    )} Kg`,
    140,
    43
  );

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor("#4d4d4d");
  doc.text("Total Berat Dimensi", 110, 49);
  doc.text(":", 138, 49);
  doc.text(
    `${formatPriceWithoutCurrency(
      Number(convertDecimalAfterComma(head.stiDesttotalVolumeWeight, 2))
    )} Kg`,
    140,
    49
  );

  let lastPage = 0;
  let lastY = 0;
  let lastHeight = 0;
  const dataLength = data.length;
  const columnsTable = [
    {
      header: "No.",
      dataKey: "no"
    },
    {
      header: "No. Bag",
      dataKey: "bagNumber"
    },
    {
      header: "No. STT",
      dataKey: "sttNumber"
    },
    {
      header: "Produk",
      dataKey: "productType"
    },
    {
      header: "Komoditas",
      dataKey: "commodity"
    },
    {
      header: "Total\nKoli",
      dataKey: "totalPieces"
    },
    {
      header: "Berat\nKotor",
      dataKey: "grossWeight"
    },
    {
      header: "Berat\nDimensi",
      dataKey: "volumeWeight"
    },
    {
      header: "Origin",
      dataKey: "origin"
    },
    {
      header: "Dest.",
      dataKey: "destination"
    },
    {
      header: "Catatan",
      dataKey: "notes"
    }
  ];

  const columnStyles = {
    0: { cellWidth: 8 },
    1: { cellWidth: 25 },
    2: { cellWidth: 25 },
    3: { cellWidth: 20 },
    4: { cellWidth: 32 },
    5: { cellWidth: 13 },
    6: { cellWidth: 13 },
    7: { cellWidth: 13 },
    8: { cellWidth: 13 },
    9: { cellWidth: 13 },
    10: { cellWidth: 18 }
  };

  doc.autoTable({
    body: data,
    columns: columnsTable,
    theme: "plain",
    startY: doc.internal.getCurrentPageInfo().pageNumber === 1 ? 55 : 0,
    margin: { top: 7, left: 10, right: 10, bottom: 7 },
    headStyles: {
      font: "helvetica",
      fontStyle: "bold",
      fillColor: "#f5f6f7",
      textColor: "#1a1421",
      fontSize: 6,
      halign: "left",
      valign: "top",
      cellPadding: { top: 4, right: 2, bottom: 4, left: 2 }
    },
    bodyStyles: {
      font: "helvetica",
      fontStyle: "normal",
      fillColor: "#fff",
      textColor: "#1a1421",
      fontSize: 6,
      halign: "left",
      valign: "top",
      lineColor: "#e0e0e0",
      lineWidth: { top: 0.1, right: 0, bottom: 0.1, left: 0 },
      cellPadding: { top: 3, right: 2, bottom: 3, left: 2 },
      cellWidth: "auto"
    },
    columnStyles: columnStyles,
    willDrawCell: (cell: any) => {
      drawCellData(cell);
      lastPage = cell.pageNumber;
      lastY = cell.cursor.y;
      lastHeight = cell.row.height;
      if (cell.row.index === dataLength - 2 && lastY + lastHeight + 20 > 270) {
        cell.cell.contentHeight = 100;
        cell.row.height = 100;
      }
    }
  });
  lastY = lastY + lastHeight + 20;
  lastY = lastY > 270 ? 270 : lastY;
  doc.setPage(lastPage);
  doc.setFontSize(8);
  doc.text("Station Admin", 9 + 90, lastY);
  doc.text("(.................................)", 5 + 90, lastY + 20);
  doc.text("Kurir", 50 + 90, lastY);
  doc.text("(.................................)", 40 + 90, lastY + 20);
  doc.text("Operations Supervisor", 80 + 90, lastY);
  doc.text("(.................................)", 80 + 90, lastY + 20);

  doc.setProperties({
    title: "STI-DEST Manifest"
  });
  doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank");
};

const manifestPdf = (printData: StiDestManifestData, logo: any) => {
  const countryCode = AccountController.accountData.account_type_detail?.countryCode;
  const data: Array<any> = [];
  let no = 1;
  for (const e of printData.stt) {
    data.push({
      no: no,
      bagNumber: e.sttBagVendorNo || e.sttBagNo,
      sttNumber: e.sttNo,
      productType: e.sttProductType,
      commodity: e.sttCommodityName,
      totalPieces: e.sttTotalPiece,
      grossWeight: `${formatByCountry(e.sttGrossWeight, { type: 'weight', country: countryCode, fractionDigits: 2 })} Kg`,
      volumeWeight: `${formatByCountry(e.sttVolumeWeight, { type: 'weight', country: countryCode, fractionDigits: 2 })} Kg`,
      origin: e.origin,
      destination: e.destination,
      notes: ""
    });
    no++;
  }
  generateManifest(printData, data, logo);
};

export default manifestPdf;
